
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface FileUploadSectionProps {
  isProcessing: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDownloadTemplate: () => void;
  onDownloadTestFile?: () => void;
}

export const FileUploadSection = ({ 
  isProcessing, 
  onFileChange, 
  onDownloadTemplate,
  onDownloadTestFile
}: FileUploadSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium">Upload Products from Excel</h3>
          <p className="text-muted-foreground">
            Upload an Excel file with your product details using our format
          </p>
        </div>
        <div className="space-x-2">
          <Button variant="outline" onClick={onDownloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
          >
            <Upload className="h-4 w-4 mr-2" />
            Select File
          </Button>
        </div>
      </div>
      
      <Alert className="bg-blue-50 border-blue-200 mb-4">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription>
          Your Excel file should have the following columns: <strong>product_name</strong> (required), 
          <strong>measuring_unit</strong>, <strong>category_name</strong>, and optionally <strong>description</strong>.
          The system supports multiple languages including Amharic (አማርኛ).
        </AlertDescription>
      </Alert>
      
      <Card className="bg-slate-50 mb-4">
        <CardContent className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h4 className="font-semibold text-sm mb-1">Having Trouble?</h4>
            <p className="text-sm text-muted-foreground">
              Download our test file with pre-formatted data (including Amharic examples) that's guaranteed to work
            </p>
          </div>
          {onDownloadTestFile && (
            <Button size="sm" variant="secondary" onClick={onDownloadTestFile}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Download Test File
            </Button>
          )}
        </CardContent>
      </Card>
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
      />
    </div>
  );
};
